/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import Analytics from '../analytics/Analytics';
import CalculationController from '../calculator/chemical-curve/CalculationController';
import { ParameterName } from '../calculator/chemical-curve/calculatorTypes';
import { getUnitPresets, getQuantities } from '../calculator/chemical-curve/configParser';
import PersistenceController from '../persistence/PersistenceController';
import UnitConverter from './UnitConverter';
import { optionsForQuantity, QuantityMapping, Unit, UnitPreset, Quantity } from './unitTypes';

class UnitController {
  private static _instance: UnitController;

  private _modes = ['metric', 'nonMetric', 'manual'];
  private _currentModeIndex = 0;

  private quantities: QuantityMapping[];
  private unitPresets: UnitPreset[];

  private unitConverter: UnitConverter;

  constructor() {
    this.unitConverter = new UnitConverter();
    this.quantities = getQuantities();
    this.unitPresets = getUnitPresets();
  }

  public static get instance() {
    return this._instance || (this._instance = new this());
  }

  public get defaultManualPreset(): UnitPreset {
    return {
      mode: 'manual',
      selections: this.unitPresets[0].selections.map((s) => ({ ...s })),
    };
  }

  public begin() {
    // add manual preset, from cache if available
    this.unitPresets.push(PersistenceController.retrieve('unitManualPreset'));
  }

  ////////////////////////////////////////////////////
  // MODE
  ////////////////////////////////////////////////////

  public get currentModeIndex() {
    return this._currentModeIndex;
  }
  public set currentModeIndex(val: number) {
    if (val < 0 || val >= this._modes.length) {
      Analytics.error('error.units', `Invalid unit mode index ${val}`);
    }
    this._currentModeIndex = val;
    Analytics.log('units.units_mode', this._modes[val]);
  }

  public get modeKeys() {
    return this._modes.map((m) => `ui.units.${m}`);
  }
  public get isManual() {
    return this._currentModeIndex === this._modes.length - 1;
  }

  ////////////////////////////////////////////////////
  // UNIT CONVERSIONS
  ////////////////////////////////////////////////////

  public get currentPreset() {
    return this.unitPresets[this.currentModeIndex];
  }
  private get manualPreset() {
    return this.unitPresets[this._modes.length - 1];
  }

  public unitForQuantity(quantityName: Quantity) {
    if (quantityName) {
      const ops = optionsForQuantity(quantityName);
      if (!ops) {
        throw new TypeError(`unitForQuantity(${quantityName}): Invalid config: no options found`);
      }
      return ops.options[0];
    }
  }

  public allUnitsForQuantity(quantityName: Quantity) {
    if (quantityName) {
      const ops = optionsForQuantity(quantityName);
      if (!ops) {
        throw new TypeError(`unitForQuantity(${quantityName}): Invalid config: no options found`);
      }
      return ops.options;
    }
  }

  public formatUnit(unit: Unit) {
    return (unit as string)
      .replace('H2O', 'H<sub>2</sub>O')
      .replace('m3', 'm<sup>3</sup>')
      .replace('ft3', 'ft<sup>3</sup>')
      .replace('ppmwdry', 'ppm<sub>w dry</sub>')
      .replace('ppmvoldry', 'ppm<sub>v dry</sub>')
      .replace('ppmw', 'ppm<sub>w</sub>')
      .replace('ppmvol', 'ppm<sub>v</sub>')
      .replace('mPa', 'MPa')
      .replace('bara', 'bar<sub>abs</sub>')
      .replace('barg', 'bar<sub>g</sub>')
      .replace('t1/2', 't<sub>½ h</sub>');
  }

  public chooseManualUnit(parameter: ParameterName, unitIndex: number) {
    if (!this.isManual) {
      throw new Error(`chooseManualUnit(${parameter}, ${unitIndex}): not in manual mode`);
    }
    const selection = this.manualPreset.selections.find((s) => s.parameter === parameter);
    if (selection) {
      selection.unitIndex = unitIndex;
      PersistenceController.store('unitManualPreset', this.manualPreset);
      Analytics.log('units.manual_unit', parameter, unitIndex);
    } else {
      throw new TypeError(`chooseManualUnit(${parameter}, ${unitIndex}): parameter not found in preset`);
    }
  }

  public convert(quantity: Quantity, value: number, from: Unit, to: Unit, isTolerance?: boolean) {
    if (from !== to) {
      if (typeof value !== 'number') return value;
      console.log('convert:', quantity, value, from, to, isTolerance);
      const converted = this.unitConverter.convert(quantity, value, from, to, isTolerance ? isTolerance : false);
      console.log('converted:', converted);
      if (converted) {
        return CalculationController.trimValue(converted);
      }
    } else {
      return value;
    }
  }

  private optionsForParameter = (parameter: ParameterName) => optionsForQuantity(this.quantityForParameter(parameter));

  private quantityForParameter(parameter: ParameterName) {
    const mapping = this.quantities.find((s) => s.parameter === parameter);
    if (!mapping) {
      throw new TypeError(`quantityForParameter(${parameter}): Invalid config: no quantity mapping found`);
    }
    return mapping.quantity;
  }

  private defaultUnitForParameter(parameter: ParameterName) {
    const ops = this.optionsForParameter(parameter);
    return ops ? ops.options[0] : '';
  }
}

export default UnitController.instance;
